import { memo, useCallback } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogTitle,
  AlertDialogHeader,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { X, Loader2, Upload, ExternalLink, AlertCircle } from "lucide-react";
import { usePublishComponentModalStore } from "./publishComponentModalStore";
import { publishComponent } from "./utils";
import { useSession } from "@clerk/clerk-react";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCanvasId } from "@/utils/utils";

const REACT_COMPONENTS_V3_URL = "https://reactcomponents.com";

export const PublishComponentModal = memo(() => {
  const { session } = useSession();
  const canvasId = useCanvasId();

  const isOpen = usePublishComponentModalStore((state) => state.isOpen);
  const isProcessing = usePublishComponentModalStore(
    (state) => state.isProcessing,
  );
  const progress = usePublishComponentModalStore((state) => state.progress);
  const progressMessage = usePublishComponentModalStore(
    (state) => state.progressMessage,
  );
  const componentId = usePublishComponentModalStore(
    (state) => state.componentId,
  );
  const error = usePublishComponentModalStore((state) => state.error);
  const storyboard = usePublishComponentModalStore((state) => state.storyboard);
  const componentName = usePublishComponentModalStore(
    (state) => state.componentName,
  );

  const closeModal = usePublishComponentModalStore((state) => state.closeModal);
  const setComponentName = usePublishComponentModalStore(
    (state) => state.setComponentName,
  );
  const startPublish = usePublishComponentModalStore(
    (state) => state.startPublish,
  );
  const setProgress = usePublishComponentModalStore(
    (state) => state.setProgress,
  );
  const setComponentId = usePublishComponentModalStore(
    (state) => state.setComponentId,
  );
  const setError = usePublishComponentModalStore((state) => state.setError);

  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );

  const handlePublish = useCallback(async () => {
    if (!componentName.trim()) {
      setError("Please enter a component name");
      return;
    }

    if (!storyboard || !canvasId || !session) {
      setError("Missing required data");
      return;
    }

    startPublish();

    try {
      const result = await publishComponent({
        storyboard,
        componentName: componentName.trim(),
        canvasId,
        session,
        treeElementLookup,
        onProgress: setProgress,
      });

      if (result.success && result.componentId) {
        setComponentId(result.componentId);
        // Auto-open react-components in a new tab
        window.open(
          `${REACT_COMPONENTS_V3_URL}/components/upload?step=1&componentFromTempoId=${result.componentId}`,
          "_blank",
        );
      } else {
        setError(result.error || "Failed to publish component");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  }, [
    componentName,
    storyboard,
    canvasId,
    session,
    treeElementLookup,
    startPublish,
    setProgress,
    setComponentId,
    setError,
  ]);

  const handleOpenChange = (open: boolean) => {
    if (!open && !isProcessing) {
      closeModal();
    }
  };

  const handleOpenInReactComponents = () => {
    if (componentId) {
      window.open(
        `${REACT_COMPONENTS_V3_URL}/components/upload?step=1&componentFromTempoId=${componentId}`,
        "_blank",
      );
    }
  };

  const isComplete = componentId !== null;
  const hasError = error !== null;
  const showNameInput = !isProcessing && !isComplete && !hasError;
  const showProgress = isProcessing;
  const showSuccess = isComplete;
  const showError = hasError && !isProcessing;

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="bg-[#1C1C1C] border-0 text-white gap-4 p-4 max-w-md">
        <AlertDialogHeader className="relative">
          <AlertDialogTitle className="text-sm flex items-center gap-2">
            <Upload size={16} className="text-primary-0" />
            Publish to React Components
          </AlertDialogTitle>
          {!isProcessing && (
            <button
              type="button"
              onClick={closeModal}
              className="text-text-secondary hover:text-text-primary absolute right-0 -top-2"
            >
              <X size={16} />
            </button>
          )}
          <AlertDialogDescription className="text-xs">
            {showNameInput &&
              "Enter a name for your component and publish it to React Components."}
            {showProgress && "Publishing your component..."}
            {showSuccess && "Your component has been published successfully!"}
            {showError && "There was an error publishing your component."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Name Input State */}
        {showNameInput && (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="componentName"
                className="text-gray-400 text-xs mb-2 block"
              >
                Component Name
              </label>
              <Input
                id="componentName"
                value={componentName}
                onChange={(e) => setComponentName(e.target.value)}
                placeholder="e.g., Button, Card, Modal"
                className="bg-background-secondary border-0 text-text-primary text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handlePublish();
                  }
                }}
              />
            </div>

            {storyboard && (
              <div className="text-gray-400 text-xs">
                <span className="text-text-secondary">Source: </span>
                <span className="font-mono">
                  {storyboard.fullFilePath?.split("/").pop() || storyboard.name}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Progress State */}
        {showProgress && (
          <div className="space-y-4">
            <Progress value={progress} className="h-2" />
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Loader2 size={12} className="animate-spin" />
              <span>{progressMessage}</span>
            </div>
          </div>
        )}

        {/* Success State */}
        {showSuccess && (
          <div className="space-y-4">
            <div className="bg-green-500/10 border border-green-500/20 rounded p-3">
              <p className="text-green-400 text-xs">
                Component published successfully! Click below to continue
                setting up your component on React Components.
              </p>
            </div>
          </div>
        )}

        {/* Error State */}
        {showError && (
          <div className="space-y-4">
            <div className="bg-red-500/10 border border-red-500/20 rounded p-3 flex items-start gap-2">
              <AlertCircle size={14} className="text-red-400 mt-0.5" />
              <p className="text-red-400 text-xs">{error}</p>
            </div>
          </div>
        )}

        <AlertDialogFooter className="gap-2">
          {showNameInput && (
            <>
              <AlertDialogCancel
                className="bg-background-secondary text-text-primary border-0 hover:bg-background-secondary/80 text-xs"
                onClick={closeModal}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault(); // Prevent dialog from closing
                  handlePublish();
                }}
                className="bg-primary-0 text-text-primary hover:bg-primary-0/80 text-xs"
                disabled={!componentName.trim()}
              >
                Publish
              </AlertDialogAction>
            </>
          )}

          {showProgress && (
            <div className="text-xs text-gray-400">
              Please wait while we publish your component...
            </div>
          )}

          {showSuccess && (
            <>
              <AlertDialogCancel
                className="bg-background-secondary text-text-primary border-0 hover:bg-background-secondary/80 text-xs"
                onClick={closeModal}
              >
                Close
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleOpenInReactComponents}
                className="bg-primary-0 text-text-primary hover:bg-primary-0/80 text-xs flex items-center gap-2"
              >
                <ExternalLink size={12} />
                Continue on React Components
              </AlertDialogAction>
            </>
          )}

          {showError && (
            <>
              <AlertDialogCancel
                className="bg-background-secondary text-text-primary border-0 hover:bg-background-secondary/80 text-xs"
                onClick={closeModal}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handlePublish}
                className="bg-primary-0 text-text-primary hover:bg-primary-0/80 text-xs"
              >
                Try Again
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
});

PublishComponentModal.displayName = "PublishComponentModal";
